import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import gsap from 'gsap';
import Icon from '../ui/Icon';
import PatternAccent from '../ui/PatternAccent';
import mountains from '../../assets/home/banner-mountains.webp';
import foodPlatter from '../../assets/home/banner-food.webp';
import bamboo from '../../assets/home/bamboo.webp';
import infoTag from '../../assets/home/info-tag.webp';

// Layers, back to front: mountain photograph, bamboo down the right edge,
// the food platter, the paper tag, then the red wave that hands off to the
// page below. Everything decorative is aria-hidden and sits in a
// pointer-events-none layer so it can never eat a click on the CTAs.

export default function Hero() {
  const { t } = useTranslation();
  const fogRef = useRef(null);
  const mountainsRef = useRef(null);
  const tagRef = useRef(null);

  // Fog lifting off the valley.
  //
  // The cream wash exists so the headline stays readable, but measured
  // against the actual photograph the left side is very pale — 16.7:1 behind
  // the headline, 7.9:1 at worst near the terraces — so the wash can clear
  // almost entirely and the type is still comfortably above AAA. What stays
  // is a soft bottom-left scrim, because that is the one area where the rice
  // terraces darken under the smaller grey text.
  //
  // Safe by construction: the fog starts VISIBLE and animates away. If gsap
  // never runs — stalled frame loop, JS error — the wash simply stays, which
  // is the readable design we had before. The opposite of an entrance
  // animation, which strands content invisible when it fails.
  useEffect(() => {
    const bands = fogRef.current?.children;
    if (!bands?.length) return undefined;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      gsap.set(bands, { opacity: 0 });
      gsap.set([mountainsRef.current, tagRef.current], { clearProps: 'all' });
      return undefined;
    }

    const tl = gsap.timeline({ delay: 0.4 });
    // Bands drift at different rates, so it reads as fog moving rather than
    // a single rectangle fading.
    tl.to(bands, {
      xPercent: 22, opacity: 0, duration: 2.6,
      ease: 'power2.inOut', stagger: 0.28,
    }, 0);
    // The valley settles out of a slight push-in as the fog goes. Scale only
    // — animating a blur across a full-bleed image is the kind of thing that
    // janks on the phones this traffic comes from.
    tl.fromTo(mountainsRef.current,
      { scale: 1.07 },
      { scale: 1, duration: 3, ease: 'power2.out' }, 0);
    // The tag is a physical paper card, so it gets placed once the scene has
    // resolved. Transform only, deliberately no opacity: gsap's from()
    // applies its start values immediately, so animating opacity here would
    // hold the tag at 0 from page load and strand it invisible if the
    // timeline never ran. A stranded transform is merely a tag sitting a few
    // pixels high — still there, still readable.
    tl.from(tagRef.current,
      { y: -18, rotate: -5, duration: 0.7, ease: 'back.out(1.7)' }, 1.6);

    return () => tl.kill();
  }, []);

  return (
    <section className="relative overflow-hidden bg-[#fdf6f2]">
      {/* Mountains */}
      <img
        ref={mountainsRef}
        src={mountains}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full origin-bottom object-cover object-bottom"
      />

      {/* Permanent scrim: only bottom-left, where the terraces darken under
          the smaller grey text. Everything above it clears. */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(253,246,242,0.92)_0%,rgba(253,246,242,0.55)_38%,transparent_68%)]" />

      {/* The fog itself — three offset banks that drift out at their own pace */}
      <div ref={fogRef} className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-y-0 -left-1/4 w-[110%] bg-gradient-to-r from-[#fdf6f2] via-[#fdf6f2] to-transparent" />
        <div className="absolute inset-y-0 left-0 w-[85%] bg-gradient-to-r from-[#fdf6f2]/95 via-[#fdf6f2]/70 to-transparent blur-xl" />
        <div className="absolute inset-y-0 left-1/4 w-[70%] bg-gradient-to-r from-[#fdf6f2]/70 to-transparent blur-2xl" />
      </div>

      {/* Balances the batik already baked into the banner's top-left corner */}
      <PatternAccent at="bottom-left" className="w-56 md:w-72" opacity={0.09} />

      {/* Bamboo, right edge. Sways from the top so the stems stay planted. */}
      <img
        src={bamboo}
        alt=""
        aria-hidden="true"
        style={{
          // The cut-out ends in a straight horizontal line mid-leaf. Fading
          // the bottom of the image out turns that hard edge into foliage
          // receding, which is what the eye expects of a plant.
          maskImage: 'linear-gradient(to bottom, #000 58%, transparent 94%)',
          WebkitMaskImage: 'linear-gradient(to bottom, #000 58%, transparent 94%)',
        }}
        className="pointer-events-none absolute -right-8 top-0 h-[115%] w-auto origin-top animate-sway opacity-90 md:-right-4 lg:right-0"
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-8 px-4 pb-28 pt-14 sm:px-6 md:pb-36 md:pt-20 lg:grid-cols-[1.05fr_1fr] lg:gap-4 lg:px-8">
        {/* ---- copy ---- */}
        <div className="relative z-10 max-w-xl">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
            {t('home.hero.eyebrow', 'Authentic Sundanese Cuisine')}
          </span>
          <h1 className="mt-4 font-serif text-4xl font-bold leading-[1.08] text-gray-900 sm:text-5xl lg:text-6xl">
            {t('home.hero.title', 'Sundanese by Tradition')}
            <span className="text-primary-600">.</span>
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-gray-600 sm:text-lg">
            {t('home.hero.subtitle')}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/menu" className="btn-primary inline-flex items-center gap-2">
              {t('home.hero.exploreMenu', 'Explore Our Menu')}
              <Icon name="arrowRight" className="h-4 w-4" />
            </Link>
            <Link
              to="/branches"
              className="inline-flex items-center gap-2 rounded-lg border border-primary-200 bg-white/80 px-6 py-3 font-medium text-primary-700 backdrop-blur transition-colors hover:border-primary-400 hover:bg-white"
            >
              <Icon name="mapPin" className="h-4 w-4" />
              {t('home.hero.findBranch', 'Find a Branch')}
            </Link>
          </div>

          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            {[
              ['sprout', t('home.hero.trustRecipes', 'Authentic Recipes')],
              ['leaf', t('home.hero.trustIngredients', 'Fresh Ingredients')],
              ['handshake', t('home.hero.trustHeritage', 'Family Heritage')],
            ].map(([icon, label]) => (
              <li key={label} className="flex items-center gap-2 text-sm text-gray-600">
                <Icon name={icon} className="h-4 w-4 text-primary-500" />
                {label}
              </li>
            ))}
          </ul>
        </div>

        {/* ---- platter + tag ---- */}
        <div className="relative z-0 lg:z-10">
          <img
            src={foodPlatter}
            alt={t('home.hero.platterAlt', 'A complete Sundanese rice platter')}
            className="mx-auto w-full max-w-[520px] drop-shadow-2xl lg:max-w-none lg:scale-[1.15] lg:translate-x-6"
            loading="eager"
            fetchpriority="high"
          />

          {/* Paper tag. The artwork is blank on purpose — the text is real
              markup so it translates and stays selectable. */}
          {/* Sits low on the platter so more of the food stays visible */}
          <div ref={tagRef} className="absolute -bottom-6 right-0 w-[132px] sm:w-[150px] lg:-right-4 lg:bottom-2">
            <div className="relative">
              <img src={infoTag} alt="" aria-hidden="true" className="w-full drop-shadow-md" />
              {/* w-full on each line: flex children default to min-width:auto,
                  so "Proudly from West Java" ran past the card instead of
                  wrapping inside it. */}
              {/* Centred in the paper's blank area rather than the whole
                  card: the artwork's lower third is the mountain line art,
                  so pb-[30%] keeps the type off it. */}
              <div className="absolute inset-0 flex flex-col items-center justify-center px-4 pb-[30%] text-center">
                <span className="w-full text-[9px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                  {t('home.hero.tagSince', 'Since')}
                </span>
                <span className="w-full font-serif text-2xl font-bold leading-none text-primary-600 sm:text-[28px]">
                  1963
                </span>
                {/* Narrower than the card so it breaks to two centred lines,
                    as in the blueprint, instead of running edge to edge. */}
                <span className="mt-1.5 w-[82%] text-[9.5px] leading-snug text-gray-500">
                  {t('home.hero.tagFrom', 'Proudly from West Java')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Red wave hand-off to the next section */}
      <svg
        className="absolute inset-x-0 bottom-0 h-16 w-full md:h-24"
        viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden="true"
      >
        <path fill="#c0392b" d="M0 62c220 46 420 46 700 12s520-30 740 6v40H0z" />
        <path fill="#ffffff" d="M0 86c240 40 460 34 720 4s500-26 720 8v22H0z" />
      </svg>
    </section>
  );
}
